import React, { useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { getAuthHeaders, getImpersonationIdentityKey, makeApiCall } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useProductPriceFormatters, type CameraWithPricing } from '../../utils/productDetailHelpers';

interface BreakdownItem {
  productId: string;
  found: boolean;
  effectivePrice?: number;
  basePrice?: number;
  salePrice?: number | null;
  saleAmount?: number | null;
  priceSource?: 'override' | 'organization' | 'rebate' | 'sale' | 'retail';
  /** How this store wants a live instant rebate to reach checkout; null when the flag is off or nothing is chosen. */
  checkoutTreatment?: 'price_reduction' | 'discount_line' | null;
  organizationPricing?: {
    scope: { kind: 'all' | 'location' | 'integration' };
    privateToOrganization: boolean;
  } | null;
}

interface BreakdownResponse {
  location: { id: string; name: string; isPrimary: boolean } | null;
  locations: Array<{ id: string; name: string; isPrimary: boolean; isActive: boolean }>;
  items: BreakdownItem[];
}

const SOURCE_LABEL: Record<NonNullable<BreakdownItem['priceSource']>, string> = {
  override: 'your override',
  organization: 'your sheet',
  rebate: 'rebate',
  sale: 'sale',
  retail: 'retail',
};

interface ByLocationEntry {
  locationId: string;
  name: string;
  isPrimary: boolean;
  breakdown: { effectivePrice: number };
  priceSource: NonNullable<BreakdownItem['priceSource']>;
  rebateProgramId: string | null;
  differsFromPrimary: boolean;
}

interface ByLocationResponse {
  productId: string;
  found: boolean;
  locations: ByLocationEntry[];
  primaryLocationId: string | null;
}

/**
 * Jordan's price for this product at one of the store's locations, resolved
 * the way a sync resolves it. Shows a location picker when the store has
 * more than one.
 */
export function StoreLocationPricePanel({ product }: { product: CameraWithPricing }) {
  const productId = product.id;
  const { t } = useAppTranslation();
  const { enabled: checkoutTreatmentEnabled } = useOrgFeatureFlag('rebate_checkout_treatment');
  const { formatAmountForProduct } = useProductPriceFormatters();
  const formatMoney = (value: number) => formatAmountForProduct(product, value) || String(value);
  const [locationId, setLocationId] = useState('');
  const { currentUser } = useAuth();
  // Included in both query keys below so an organization switch or an admin
  // entering/leaving impersonation invalidates these caches immediately,
  // instead of showing the previously-acting store's price for the same
  // catalog product (see usePriceVariesByLocation for the same identity key
  // used by the product-card "varies by location" chip).
  const identityKey = `${currentUser?.uid || 'anonymous'}|${getImpersonationIdentityKey()}`;
  const { data } = useQuery({
    queryKey: ['store-price-breakdown', identityKey, productId, locationId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(
        `/store/pricing/breakdown?productIds=${encodeURIComponent(productId)}${locationId ? `&locationId=${encodeURIComponent(locationId)}` : ''}`,
        { headers }
      );
      if (!response.ok) return null;
      return ((await response.json()).data || null) as BreakdownResponse | null;
    },
    staleTime: 30_000,
  });

  const activeLocations = data?.locations?.filter((l) => l.isActive) ?? [];
  const hasMultipleLocations = activeLocations.length > 1;

  const { data: byLocation } = useQuery({
    queryKey: ['store-price-by-location', identityKey, productId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(
        `/store/pricing/breakdown/by-location?productId=${encodeURIComponent(productId)}`,
        { headers }
      );
      if (!response.ok) return null;
      return ((await response.json()).data || null) as ByLocationResponse | null;
    },
    // No point resolving per-location pricing for a store with one location.
    enabled: hasMultipleLocations,
    staleTime: 30_000,
  });

  const item = data?.items?.[0];
  if (!data || !item || !item.found || item.effectivePrice == null) return null;
  const source = item.priceSource ? SOURCE_LABEL[item.priceSource] : null;
  const fromSheet = item.priceSource === 'organization' && item.organizationPricing;

  return (
    <Box sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider', p: 1.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary">
            Your price{data.location ? ` at ${data.location.name}` : ''}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="h6" fontWeight={700}>{formatMoney(item.effectivePrice)}</Typography>
            {source && <Chip size="small" variant="outlined" label={source} />}
            {fromSheet && item.organizationPricing?.privateToOrganization && <Chip size="small" variant="outlined" label="vendor sheet" />}
            {fromSheet && item.organizationPricing?.scope.kind !== 'all' && <Chip size="small" variant="outlined" label="this location" />}
          </Stack>
        </Box>
        {hasMultipleLocations && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id={`price-location-${productId}`}>Location</InputLabel>
            <Select
              labelId={`price-location-${productId}`}
              label="Location"
              value={locationId || data.location?.id || ''}
              onChange={(e) => setLocationId(e.target.value)}
            >
              {activeLocations.map((l) => (
                <MenuItem key={l.id} value={l.id}>{l.name}{l.isPrimary ? ' (primary)' : ''}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {checkoutTreatmentEnabled && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('pricing.checkoutTreatment.title')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              color={item.checkoutTreatment ? 'default' : 'warning'}
              label={
                item.checkoutTreatment === 'discount_line'
                  ? t('pricing.checkoutTreatment.discountLine')
                  : item.checkoutTreatment === 'price_reduction'
                    ? t('pricing.checkoutTreatment.priceReduction')
                    : t('pricing.checkoutTreatment.notChosen')
              }
            />
            {item.priceSource === 'rebate' && item.basePrice != null && item.saleAmount != null && (
              <Typography variant="body2" color="text.secondary">
                {t('pricing.checkoutTreatment.rebateDetail', {
                  preRebate: formatMoney(item.basePrice),
                  rebate: formatMoney(item.saleAmount),
                })}
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {hasMultipleLocations && byLocation?.found && byLocation.locations.length > 1 && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {t('pricing.priceByLocation.title')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 0 }}>{t('pricing.priceByLocation.location')}</TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>{t('pricing.priceByLocation.price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byLocation.locations.map((entry) => (
                  <TableRow key={entry.locationId}>
                    <TableCell sx={{ pl: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2">{entry.name}{entry.isPrimary ? ' (primary)' : ''}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                        <Typography variant="body2" fontWeight={600}>{formatMoney(entry.breakdown.effectivePrice)}</Typography>
                        <Chip size="small" variant="outlined" label={SOURCE_LABEL[entry.priceSource]} />
                        {entry.rebateProgramId && (
                          <Chip size="small" variant="outlined" color="success" label={t('pricing.priceByLocation.program')} />
                        )}
                        {!entry.differsFromPrimary && !entry.isPrimary && (
                          <Typography variant="caption" color="text.disabled">
                            {t('pricing.priceByLocation.sameAsPrimary')}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
